/**
 * AuthProvider — single source of truth for the current user + auth flow state.
 *
 * Mode is "local" today (email + password + TOTP MFA against /api/auth/*).
 * When MS Entra SSO lands we add mode="sso" without changing downstream
 * components; they just consume `useAuth()`.
 *
 * Flow states (matches backend challenges):
 *   - "unauthenticated"        no session
 *   - "password_reset_required" temp password worked, must reset
 *   - "mfa_enroll_required"    password ok, must enroll TOTP
 *   - "mfa_required"           returning user, must enter TOTP code
 *   - "authenticated"          fully logged in
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, ApiError } from '../api/client';

const AuthContext = createContext(null);

export const AUTH_MODE = 'local'; // toggle to 'sso' when MS Entra lands

const FLOW = {
  UNAUTHENTICATED:         'unauthenticated',
  PASSWORD_RESET_REQUIRED: 'password_reset_required',
  MFA_ENROLL_REQUIRED:     'mfa_enroll_required',
  MFA_REQUIRED:            'mfa_required',
  AUTHENTICATED:           'authenticated',
};

export { FLOW };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [flow, setFlow] = useState(FLOW.UNAUTHENTICATED);
  const [loading, setLoading] = useState(true);

  // Map a `challenge` string (from /auth/login OR /auth/me on refresh) to a
  // FLOW state. Shared so mount-hydration and login responses agree.
  const flowForChallenge = useCallback((challenge) => {
    if (challenge === 'password_reset_required') return FLOW.PASSWORD_RESET_REQUIRED;
    if (challenge === 'mfa_enroll_required')     return FLOW.MFA_ENROLL_REQUIRED;
    if (challenge === 'mfa_required')            return FLOW.MFA_REQUIRED;
    return FLOW.AUTHENTICATED;
  }, []);

  // On mount, try to hydrate the session via /auth/me (cookie-based).
  //
  // /auth/me may succeed with a PRE-MFA (low-privilege) cookie during the
  // login flow. It reports the pending step via `challenge`, so a page refresh
  // mid-enrollment re-enters the correct flow state instead of jumping to
  // AUTHENTICATED (the refresh-bypass bug). If /me 401s, we're logged out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (!cancelled) {
          setUser(me);
          setFlow(flowForChallenge(me?.challenge));
        }
      } catch (e) {
        // Expected when not yet logged in — silent.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [flowForChallenge]);

  const applyLoginResponse = useCallback((resp) => {
    // Backend returns { challenge?, user }
    setUser(resp.user || null);
    setFlow(flowForChallenge(resp.challenge));
  }, [flowForChallenge]);

  const login = useCallback(async (email, password) => {
    const resp = await authApi.login(email, password);
    applyLoginResponse(resp);
    return resp;
  }, [applyLoginResponse]);

  const completePasswordReset = useCallback(async (newPassword) => {
    // The user is already authenticated via the low-privilege cookie issued at
    // /auth/login; backend doesn't require the current password again.
    const resp = await authApi.passwordReset(newPassword);
    // Backend returns the next challenge (typically `mfa_enroll_required`).
    applyLoginResponse(resp);
    return resp;
  }, [applyLoginResponse]);

  const startMfaEnrollment = useCallback(async () => {
    // Returns { qr_code, provisioning_uri, recovery_codes }
    return authApi.mfaEnrollStart();
  }, []);

  const completeMfaEnrollment = useCallback(async (code) => {
    const resp = await authApi.mfaEnrollVerify(code);
    // Backend returns { status: 'ok', user } with no `challenge` →
    // applyLoginResponse falls through to AUTHENTICATED.
    applyLoginResponse(resp);
    return resp;
  }, [applyLoginResponse]);

  const verifyMfa = useCallback(async (code) => {
    const resp = await authApi.mfaVerify(code);
    // Backend returns { status: 'ok', user } with no `challenge` →
    // applyLoginResponse falls through to AUTHENTICATED.
    applyLoginResponse(resp);
    return resp;
  }, [applyLoginResponse]);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch (_) { /* ignore */ }
    setUser(null);
    setFlow(FLOW.UNAUTHENTICATED);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      setFlow(flowForChallenge(me?.challenge));
      return me;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null);
        setFlow(FLOW.UNAUTHENTICATED);
      }
      throw e;
    }
  }, [flowForChallenge]);

  const value = useMemo(() => ({
    mode: AUTH_MODE,
    loading,
    user,
    flow,
    setFlow,
    login,
    completePasswordReset,
    startMfaEnrollment,
    completeMfaEnrollment,
    verifyMfa,
    logout,
    refreshMe,
    isAuthenticated: flow === FLOW.AUTHENTICATED,
    role: user?.role || null,
  }), [loading, user, flow, login, completePasswordReset, startMfaEnrollment, completeMfaEnrollment, verifyMfa, logout, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
